import React from 'react';
import styled from 'styled-components';
import { CumaButtonType } from '../button/CumaButtonType';
import { ModalStyle } from 'src/admin-entity/interface/modal/modal-style.interface';
import { useModalState } from '../../../../context/ModalContext';

const WithTitleContainer = styled.div`
    width: 296px;
    padding: 24px;
    display: flex;
    gap: 16px;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;

    .title {
        font-weight: 700;
        font-size: 18px;
        line-height: 21px;
    }

    .contents {
        font-weight: 400;
        font-size: 14px;
        line-height: 22px;
        white-space: pre-line;
    }

    .buttons {
        display: flex;
        gap: 8px;
    }
`;

const SingleLineContainer = styled.div`
    width: 296px;
    padding: 24px;
    display: flex;
    gap: 24px;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;

    .contents {
        padding: 12px 0px;
        font-weight: 400;
        font-size: 15px;
        line-height: 18px;
        text-align: center;
    }
`;

const SuccessContainer = styled.div`
    width: 296px;
    padding: 24px;
    display: flex;
    gap: 24px;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;

    .success {
        display: flex;
        gap: 12px;
        flex-direction: column;
        align-items: center;
        font-weight: 400;
        font-size: 15px;
        line-height: 18px;
    }
`;

const WarningContainer = styled.div`
    width: 296px;
    padding: 24px;
    display: flex;
    gap: 24px;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;

    .warn {
        display: flex;
        gap: 12px;
        flex-direction: column;
        align-items: center;
        font-weight: 400;
        font-size: 15px;
        line-height: 18px;

        .text {
            white-space: pre-line;
        }
    }
`;

const FailureContainer = styled.div`
    width: 296px;
    padding: 24px;
    display: flex;
    gap: 24px;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;

    .failure {
        display: flex;
        gap: 12px;
        flex-direction: column;
        align-items: center;
        font-weight: 400;
        font-size: 15px;
        line-height: 18px;
    }
`;

const OnlyTextContainer = styled.div`
    width: 296px;
    padding: 24px;
    display: flex;
    gap: 24px;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;

    font-size: 15px;
    line-height: 18px;
    text-align: center;
    white-space: pre-wrap;
`;

interface Props {
    modalStyleType: ModalStyle;
    confirmButtonDisabled?: boolean;
}

function CumaModalContents({ modalStyleType, confirmButtonDisabled = false }: Props) {
    const { modalStyle, setModalStyle } = useModalState();

    const onClickConfirm = React.useCallback(() => {
        if ('onClickConfirmButton' in modalStyleType) {
            modalStyleType.onClickConfirmButton();
        }
        setModalStyle(null);
    }, [modalStyleType]);

    const onClickCancel = React.useCallback(() => {
        if ('onClickCancelButton' in modalStyleType) {
            modalStyleType.onClickCancelButton();
        }
        setModalStyle(null);
    }, [modalStyleType]);

    if (!modalStyle && !modalStyleType) {
        return <></>;
    }

    switch (modalStyleType.type) {
        case 'withTitle':
            return (
                <WithTitleContainer>
                    <div className="title">{modalStyleType.title}</div>
                    <div className="contents">{modalStyleType.contents}</div>
                    <div className="buttons">
                        <CumaButtonType $cumaButtonStyle="light-gray-outline" size="md" onClick={onClickCancel}>
                            {modalStyleType.cancelButtonContents}
                        </CumaButtonType>
                        <CumaButtonType
                            $cumaButtonStyle="primary"
                            size="md"
                            onClick={onClickConfirm}
                            disabled={confirmButtonDisabled}
                        >
                            {modalStyleType.confirmButtonContents}
                        </CumaButtonType>
                    </div>
                </WithTitleContainer>
            );
        case 'singleLine':
            return (
                <SingleLineContainer>
                    <div className="contents">{modalStyleType.contents}</div>
                    <CumaButtonType
                        onClick={onClickConfirm}
                        size="md"
                        $cumaButtonStyle="primary"
                        disabled={confirmButtonDisabled}
                    >
                        확인
                    </CumaButtonType>
                </SingleLineContainer>
            );
        case 'success':
            return (
                <SuccessContainer>
                    <div className="success">
                        <img
                            src={`https://s3.ap-northeast-2.amazonaws.com/www.cuma.co.kr/icon/ic_check_green.svg`}
                            width={60}
                            height={60}
                            alt=""
                        />
                        <div>{modalStyleType.contents}</div>
                    </div>
                    <CumaButtonType
                        onClick={onClickConfirm}
                        size="md"
                        $cumaButtonStyle="primary"
                        disabled={confirmButtonDisabled}
                    >
                        확인
                    </CumaButtonType>
                </SuccessContainer>
            );
        case 'warning':
            return (
                <>
                    <WarningContainer>
                        <div className="warn">
                            <img
                                src={`https://s3.ap-northeast-2.amazonaws.com/www.cuma.co.kr/icon/ic_info_yellow.svg`}
                                width={60}
                                height={60}
                                alt=""
                            />
                            <div className="text">{modalStyleType.contents}</div>
                        </div>
                        <CumaButtonType
                            disabled={confirmButtonDisabled}
                            onClick={onClickConfirm}
                            size="md"
                            $cumaButtonStyle="primary"
                        >
                            확인
                        </CumaButtonType>
                    </WarningContainer>
                </>
            );
        case 'failure':
            return (
                <FailureContainer>
                    <div className="failure">
                        <img
                            src={`https://s3.ap-northeast-2.amazonaws.com/www.cuma.co.kr/icon/ic_failure_red.svg`}
                            width={60}
                            height={60}
                            alt=""
                        />
                        <div>{modalStyleType.contents}</div>
                    </div>
                    <CumaButtonType
                        onClick={onClickConfirm}
                        size="md"
                        $cumaButtonStyle="primary"
                        disabled={confirmButtonDisabled}
                    >
                        확인
                    </CumaButtonType>
                </FailureContainer>
            );
        case 'onlyText':
            return (
                <OnlyTextContainer>
                    <div>{modalStyleType.contents}</div>
                </OnlyTextContainer>
            );
        default:
            return <></>;
    }
}

export default CumaModalContents;
